"use client";

import { useEffect, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import { TalentSearchProvider } from "@/components/search/talent-search-provider";
import { TalentSearchFiltersProvider } from "@/components/search/talent-search-filters-provider";
import { SidebarInset } from "@/components/layout/sidebar/sidebar-layout";
import { Sidebar } from "@/components/layout/sidebar";
import { Navbar } from "@/components/layout/navbar";
import { OnboardingGuard } from "@/components/layout/onboarding-guard";
import { PageLoader } from "@/components/page-loader";
import { SearchHistory } from "@/components/search/search-history";
import { ProjectSequencesSheet } from "@/features/sequences/components/project-sequences-sheet";
import { MaintenanceBanner } from "@/components/layout/maintenance-banner";
import { Icon } from "@/components/ui/icon";
import { useAuth } from "@/features/auth";
import { useSelectedProject } from "@/hooks/use-search";
import { useProjectSearchRunsStream } from "@/hooks/use-project-search-runs-stream";
import { useConversationsFeature } from "@/features/conversations/hooks/use-conversations-feature";
import { useProjectConversationsStream } from "@/features/conversations/hooks/use-project-conversations-stream";
import { SuspendedScreen } from "@/features/billing/components/suspended-screen";
import { useProjectSwitchStore } from "@/features/projects/hooks/use-project-switch";
import { NotificationPollProvider } from "@/features/notifications/components/notification-poll-provider";
import { ExportJobsHost } from "@/features/exports/tracker";
import { WelcomeModal } from "@/features/onboarding";
import { TourOverlay } from "@/features/tours";
import { Enrollment } from "@/features/enrollment";
import { UserbackProvider } from "@/components/userback-provider";
import { IdentifyCurrentUser } from "@/lib/observability/identify-current-user";
import { SignupAttributionSync } from "@/features/auth/components/signup-attribution-sync";

const ORG_EXEMPT_PATHS = ["/settings/profile", "/settings/appearance", "/settings/connected-accounts"];

const SUSPENSION_EXEMPT_PATHS = [
  "/settings/plans-billing",
  "/settings/users",
  "/settings/profile",
  "/settings/appearance",
  "/settings/connected-accounts",
];

const HARD_LOCK_STATUSES = ["admin", "plan_expired"];

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, organization, isLoading, selectedProjectId } = useAuth();
  const { data: selectedProject, isLoading: projectLoading } = useSelectedProject();
  const router = useRouter();
  const pathname = usePathname();
  const showProjectLoader = useProjectSwitchStore((s) => s.showLoader);

  const projectReady = !selectedProjectId || !!selectedProject;
  const coreDataReady = !!user && (!projectLoading || projectReady);

  const suspensionStatus =
    (organization as Record<string, unknown> | null)?.suspension_status as
      | string
      | null;

  const needsOrg =
    !!user &&
    !isLoading &&
    !organization &&
    !ORG_EXEMPT_PATHS.some((p) => pathname.startsWith(p));

  const isHardLocked =
    !!suspensionStatus && HARD_LOCK_STATUSES.includes(suspensionStatus);

  const onExemptPath =
    !isHardLocked &&
    SUSPENSION_EXEMPT_PATHS.some((p) => pathname.startsWith(p));

  const isSuspended =
    !!organization && !!suspensionStatus && !onExemptPath;

  const showSuspensionBanner =
    !!organization && !!suspensionStatus && onExemptPath;

  useEffect(() => {
    if (needsOrg) {
      router.replace("/onboarding");
    }
  }, [needsOrg, router]);

  if (isSuspended) {
    return <SuspendedScreen />;
  }

  return (
    <>
      <PageLoader
        ready={coreDataReady}
        error={!isLoading && !user}
        forceShow={showProjectLoader}
      />
      <OnboardingGuard>
        <TalentSearchProvider>
          <TalentSearchFiltersProvider>
            {/* Desktop: the shell is the darker "frame" the sidebar blends
                into; page content sits in the inset panel (SidebarInset). */}
            <div className="flex h-svh w-full flex-col bg-surface-primary lg:bg-surface-frame">
              <BannerStack>
                <MaintenanceBanner />
                {showSuspensionBanner && (
                  <SuspensionBanner
                    status={suspensionStatus!}
                    onResolve={() => router.push("/settings/plans-billing")}
                    isOnBilling={pathname.startsWith("/settings/plans-billing")}
                  />
                )}
              </BannerStack>
              <div className="flex min-h-0 flex-1">
                <Sidebar />
                <SidebarInset className="flex min-w-0 flex-1">
                  <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                    <Navbar />
                    <main className="flex-1 overflow-y-auto">{children}</main>
                  </div>
                </SidebarInset>
              </div>
            </div>
            <SearchHistory />
            <ProjectSequencesSheet />
            <NotificationPollProvider />
            {/* Single global mount of the per-project search-run stream; it
                feeds the runs store that every phase indicator reads from. */}
            <SearchRunsStream projectId={selectedProject?.id ?? null} />
            <ConversationsStream projectId={selectedProject?.id ?? null} />
            {/* Single global mount: tracks every export job above the routes, so
                a job outlives the page that started it. */}
            <ExportJobsHost />
            <WelcomeModal />
            {/* Single global mount of the guided-tour overlay; tours are
                started per-area via useAutoStartTour / startTour. */}
            <TourOverlay />
            {/* Single global mount of the enrollment review drawer. Every
                enrollment entry point (pipeline bulk, pipeline profile,
                sequence Enrollments tab, talent search) hands off to this
                one drawer via `usePreviewStore().openDrawer(...)`, so it
                must be mounted exactly once for the whole dashboard. */}
            <Enrollment.PreviewDrawer />
            <UserbackProvider />
            <IdentifyCurrentUser />
            <SignupAttributionSync />
          </TalentSearchFiltersProvider>
        </TalentSearchProvider>
      </OnboardingGuard>
    </>
  );
}

/** Keeps the stream's `connected` state changes out of the layout's render. */
function SearchRunsStream({ projectId }: { projectId: string | null }) {
  useProjectSearchRunsStream(projectId);
  return null;
}

/** Same isolation as SearchRunsStream; gated on the Meetings plan feature. */
function ConversationsStream({ projectId }: { projectId: string | null }) {
  const enabled = useConversationsFeature();
  useProjectConversationsStream(enabled ? projectId : null);
  return null;
}

function BannerStack({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      const h = el.offsetHeight;
      document.documentElement.style.setProperty(
        "--banner-offset",
        h > 0 ? `${h}px` : "0px",
      );
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--banner-offset");
    };
  }, []);

  return <div ref={ref}>{children}</div>;
}

function SuspensionBanner({
  status,
  onResolve,
  isOnBilling,
}: {
  status: string;
  onResolve: () => void;
  isOnBilling: boolean;
}) {
  const isPastDue = status === "past_due";
  const message = isPastDue
    ? "Your latest payment failed. Update your payment method to restore access for your team."
    : "Your subscription has been cancelled. Reactivate your plan to restore access.";
  const actionLabel = isPastDue
    ? "Fix payment"
    : "Reactivate";

  return (
    <div
      className="flex w-full shrink-0 items-center justify-center gap-2 border-b px-4 py-2 text-[13px] font-medium leading-snug"
      style={{
        backgroundColor: isPastDue
          ? "rgba(248, 113, 113, 0.10)"
          : "rgba(255, 175, 83, 0.10)",
        color: isPastDue ? "#F87171" : "#FFAF53",
        borderColor: isPastDue
          ? "rgba(248, 113, 113, 0.20)"
          : "rgba(255, 175, 83, 0.20)",
      }}
    >
      <Icon
        name={isPastDue ? "IconCreditCard2" : "IconWarningSign"}
        size={16}
      />
      <span>{message}</span>
      {!isOnBilling && (
        <button
          type="button"
          onClick={onResolve}
          className="ml-1 inline-flex cursor-pointer items-center gap-1 underline underline-offset-2 transition-opacity hover:opacity-80"
        >
          {actionLabel}
          <Icon name="IconArrowUpRight" size={14} />
        </button>
      )}
    </div>
  );
}
